/**
 * Hook to capture page context and browser information
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { PageContext, BrowserInfo, CapturedContext, EntityContext, UserInfo } from '../types';
import { useConsoleCapture } from './useConsoleCapture';
import { useNetworkCapture } from './useNetworkCapture';

interface UseContextCaptureOptions {
  appName: string;
  captureConsoleErrors: boolean;
  captureNetworkErrors: boolean;
  captureEntities: boolean;
  maxConsoleErrors: number;
  maxNetworkErrors: number;
  redactPatterns: RegExp[];
  getUserInfo?: () => UserInfo | null;
  getSelectedEntities?: () => EntityContext[];
}

interface UseContextCaptureResult {
  context: CapturedContext;
  refreshContext: () => void;
  clearErrors: () => void;
  addEntity: (entity: EntityContext) => void;
  removeEntity: (entityId: string) => void;
}

function getPageContext(): PageContext {
  const url = window.location.pathname;
  const fullUrl = window.location.href;
  const title = document.title;
  const hash = window.location.hash || undefined;

  // Parse query params
  const queryParams: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  // Detect module and feature from URL
  const pathParts = url.split('/').filter(Boolean);
  const module = pathParts[0] || undefined;
  const feature = pathParts[1] || undefined;

  return {
    url,
    fullUrl,
    title,
    module,
    feature,
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    hash,
  };
}

function getBrowserInfo(): BrowserInfo {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
  };
}

export function useContextCapture(options: UseContextCaptureOptions): UseContextCaptureResult {
  const {
    appName,
    captureConsoleErrors,
    captureNetworkErrors,
    captureEntities,
    maxConsoleErrors,
    maxNetworkErrors,
    redactPatterns,
    getUserInfo,
    getSelectedEntities,
  } = options;

  const [pageContext, setPageContext] = useState<PageContext>(getPageContext);
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo>(getBrowserInfo);
  const [customEntities, setCustomEntities] = useState<EntityContext[]>([]);

  // Console error capture
  const {
    errors: consoleErrors,
    clearErrors: clearConsoleErrors,
  } = useConsoleCapture({
    enabled: captureConsoleErrors,
    maxErrors: maxConsoleErrors,
  });

  // Network error capture
  const {
    errors: networkErrors,
    clearErrors: clearNetworkErrors,
  } = useNetworkCapture({
    enabled: captureNetworkErrors,
    maxErrors: maxNetworkErrors,
    redactPatterns,
  });

  // Refresh page context on navigation
  const refreshContext = useCallback(() => {
    setPageContext(getPageContext());
    setBrowserInfo(getBrowserInfo());
  }, []);

  // Listen for navigation changes
  useEffect(() => {
    const handleNavigation = () => {
      refreshContext();
    };

    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);

    // Also handle SPA navigation via history API
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleNavigation();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleNavigation();
    };

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [refreshContext]);

  // Clear all errors
  const clearErrors = useCallback(() => {
    clearConsoleErrors();
    clearNetworkErrors();
  }, [clearConsoleErrors, clearNetworkErrors]);

  // Entity management
  const addEntity = useCallback((entity: EntityContext) => {
    setCustomEntities((prev) => {
      const filtered = prev.filter((e) => e.id !== entity.id);
      return [...filtered, entity];
    });
  }, []);

  const removeEntity = useCallback((entityId: string) => {
    setCustomEntities((prev) => prev.filter((e) => e.id !== entityId));
  }, []);

  // Get all entities (custom + from app)
  const allEntities = useMemo(() => {
    const appEntities = captureEntities && getSelectedEntities ? getSelectedEntities() : [];
    return [...customEntities, ...appEntities];
  }, [captureEntities, getSelectedEntities, customEntities]);

  // Build full context
  const context: CapturedContext = useMemo(() => {
    const user = getUserInfo?.() ?? undefined;

    return {
      page: pageContext,
      browser: browserInfo,
      user,
      appName,
      consoleErrors: captureConsoleErrors ? consoleErrors : [],
      networkErrors: captureNetworkErrors ? networkErrors : [],
      entities: allEntities,
      capturedAt: new Date().toISOString(),
    };
  }, [
    pageContext,
    browserInfo,
    appName,
    captureConsoleErrors,
    consoleErrors,
    captureNetworkErrors,
    networkErrors,
    allEntities,
    getUserInfo,
  ]);

  return {
    context,
    refreshContext,
    clearErrors,
    addEntity,
    removeEntity,
  };
}
